import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import { FormikProvider, Form, useFormik, FormikErrors, yupToFormErrors } from 'formik';
import {
  BubbleAlert,
  BubbleButton,
  BubbleFormGroup,
  BubbleImageSelector,
  BubbleInput,
  BubbleSelect,
  BubbleTextArea,
  BubbleTypeahead,
  BubbleTypeaheadSimple,
} from '../BubbleUI';
import { Editor } from '..';
import * as Yup from 'yup';
import {
  ALL_INFO_CONTENT_APPEARANCES,
  BareContentTemplate,
  ContentTemplate,
  InfoContentTemplate,
  MessageContentTemplate,
  OfferContentTemplate,
} from '@go-shared/types/contentTemplate';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { ObjectID } from 'bson';
import { create, update } from '@go-client/lib/api/contentTemplate';
import { updateContentTags } from '@go-client/lib/api/property';
import { mapBy, MappedItems, notUndefined } from '@go-shared/lib/functional';
import { BubbleTypeaheadOption, BubbleTypeaheadValue } from '../BubbleUI/BubbleTypeahead';
import { PropertyTag } from '@go-shared/types/property';
import uuid from 'uuid';
import { Alert } from '@spark-labs/avocado-ui';
import { createPortal } from 'react-dom';
import { canModifyMultiPropertyItem } from '@go-shared/lib/permissions';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import McmVisibilityTable from '../McmVisibilityTable';
import { Mcm } from '@go-shared/types/mcm';
import { camelToSentenceCase } from '@go-shared/lib/string';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AudienceVisibilityTable from '../AudienceVisibilityTable';
import { Audience } from '@go-shared/types/audience';
import { CallToActions } from '@go-client/components/BasicForms/CallToActions';
import { DailyProgramCta } from '@go-shared/types/dailyProgram';
import { fme } from '@go-shared/lib/utils';

interface ContentTemplateFormikForm {
  name: string;
  content: string;
  showHtml: boolean;
  appearance?: InfoContentTemplate['appearance'];
  mcm?: Mcm;
  audience?: Audience;
  description?: string;
  images?: string[];
  type?: BareContentTemplate['type'];
  tags: BubbleTypeaheadValue<PropertyTag>[];
  audienceZoneFilter?: string;
  descriptionFilter: string;
  hideFromDigitalDailyProgram?: boolean;
  callToActions: DailyProgramCta[];

  segment?: string;
  levels: string[];
  attributes: string[];
}

function GENERATE_INITIAL_VALUES(
  tagMap: MappedItems<{ key: string; item: PropertyTag }>,
  skipBasic?: boolean,
  edit?: ContentTemplate
): ContentTemplateFormikForm {
  let type = edit?.type;
  if (type === undefined) {
    type = skipBasic ? 'offer' : 'basic';
  }

  const values: ContentTemplateFormikForm = {
    name: edit?.name ?? '',
    content: edit?.content ?? '',
    showHtml: false,
    type,
    mcm: edit?.mcm,
    audience: edit?.audience,
    hideFromDigitalDailyProgram: edit?.hideFromDigitalDailyProgram,
    tags: (edit?.tags || [])
      .map(k => {
        const match = tagMap[k];

        if (!match) return undefined;

        return {
          key: uuid(),
          refKey: match.key,
          item: match.item,
        };
      })
      .filter(notUndefined),
    descriptionFilter: '',
    callToActions: edit?.callToActions || [],
    segment: edit?.segment?._id || undefined,
    levels: edit?.levels.map(l => l._id) || [],
    attributes: edit?.attributes.map(a => a._id) || [],
  };

  if (edit?.type === 'offer') {
    values.description = (edit as unknown as OfferContentTemplate).description;
    values.images = (edit as unknown as OfferContentTemplate).images;
  }

  if (edit?.type === 'message') {
    values.description = (edit as unknown as MessageContentTemplate).description;
  }

  if (edit?.type === 'info') {
    values.description = (edit as unknown as InfoContentTemplate).description;
    values.appearance = (edit as unknown as InfoContentTemplate).appearance;
  }

  return values;
}

export interface ContentTemplateFormikMode {
  type: 'create' | 'edit' | 'view' | 'filter';
  edit?: ContentTemplate;
}

export interface CntTmplFormikExtState {
  values: ContentTemplateFormikForm;
  disabled: boolean;
}

interface ContentTemplateFormikPropsBase {
  mode: ContentTemplateFormikMode;
  actionsElem?: HTMLDivElement;
  actionsClassName?: string;
  actionsAppearance?: 'drawer-float';
  subnavClassName?: string;
  skipBasic?: boolean;
  onSubmit?: (template: BareContentTemplate) => void;
  onStateUpdate?: (state: CntTmplFormikExtState) => void;
}

export interface ContentTemplateFormikRef {
  reset: () => void;
  submit: () => void;
}

type ContentTemplateFormikProps = ContentTemplateFormikPropsBase;

function ContentTemplateFormik(
  props: ContentTemplateFormikProps,
  ref: React.ForwardedRef<ContentTemplateFormikRef>
) {
  const { user } = useUserInfo();
  const serverInfo = useServerInfo();
  const { brand } = serverInfo;
  const { activeProperty: property, refreshContext: refreshActiveProperty } = useActiveProperty();

  const contentTagOpts = property.contentTags.map(t => ({
    key: t._id,
    item: t,
  }));
  const contentTagMap = mapBy(contentTagOpts, 'key');

  const segmentsMap = mapBy(brand.contentSegments, '_id');
  const levelsMap = mapBy(brand.contentLevels, '_id');
  const attributesMap = mapBy(brand.contentAttributes, '_id');

  const formik = useFormik({
    initialValues: GENERATE_INITIAL_VALUES(contentTagMap, props.skipBasic, props.mode.edit),
    validate: async values => {
      let errors: FormikErrors<ContentTemplateFormikForm> = {};

      const basicSchema = Yup.object({
        name: Yup.string().required('Required'),
      });

      try {
        await basicSchema.validate(values, { abortEarly: false });
      } catch (err) {
        const t = yupToFormErrors<ContentTemplateFormikForm>(err);
        errors = { ...errors, ...t };
      }

      switch (values.type) {
        case 'offer':
        case 'message':
        case 'info':
          if (!values.description) {
            errors.description = 'Required';
          }
          break;
      }

      return errors;
    },
    onSubmit: onFormSubmit,
  });

  useEffect(() => {
    formik.resetForm({
      values: GENERATE_INITIAL_VALUES(contentTagMap, props.skipBasic, props.mode.edit),
    });
  }, [props.mode.edit]);

  const { values, errors, touched, setFieldValue, handleSubmit, handleChange, handleBlur, dirty } =
    formik;

  const actionClasses = [];
  if (props.actionsAppearance === 'drawer-float')
    actionClasses.push('sticky mt-3 bottom-0 z-20 -mx-8 p-8 pt-4 form-blur');
  if (props.actionsClassName) actionClasses.push(props.actionsClassName);

  const subnavClasses = ['flex items-center mb-6'];
  if (props.subnavClassName) subnavClasses.push(props.subnavClassName);

  async function onFormSubmit(values: ContentTemplateFormikForm) {
    let newContentTmpl: BareContentTemplate = {
      ...(props.mode.edit || {
        _id: new ObjectID().toHexString(),
        properties: [{ property: property._id }],
        source: property._id,
      }),
      brand: property.brand._id,
      name: values.name,
      content: values.content,
      tags: values.tags.map(t => t.item._id),
      type: 'basic',
      mcm: values.mcm,
      audience: values.audience,
      hideFromDigitalDailyProgram: values.hideFromDigitalDailyProgram,
      callToActions: values.callToActions,
      segment: values.segment || null,
      levels: values.levels || [],
      attributes: values.attributes || [],
    };

    switch (values.type) {
      case 'offer':
        newContentTmpl = {
          ...newContentTmpl,
          type: 'offer',
          description: values.description || '',
          images: values.images || [],
        };
        break;
      case 'message':
        newContentTmpl = {
          ...newContentTmpl,
          type: 'message',
          description: values.description || '',
        };
        break;
      case 'info':
        newContentTmpl = {
          ...newContentTmpl,
          type: 'info',
          description: values.description || '',
          appearance: values.appearance || 'card',
        };
        break;
    }

    switch (props.mode.type) {
      case 'create':
        const { _id, ...rest } = newContentTmpl;
        newContentTmpl = await create(rest);
        break;
      case 'edit':
        newContentTmpl = await update(newContentTmpl);
        break;
    }

    if (props.onSubmit) props.onSubmit(newContentTmpl);
  }

  async function handleOnCreateTag(name: string) {
    formik.setSubmitting(true);

    try {
      const newTag = {
        _id: new ObjectID().toHexString(),
        name,
      };

      await updateContentTags(property._id, [...(property.contentTags || []), newTag]);
      await refreshActiveProperty();

      setFieldValue('tags', [...values.tags, { key: uuid(), refKey: newTag._id, item: newTag }]);
    } finally {
      formik.setSubmitting(false);
    }
  }

  async function handleOnRemoveTag(opt: BubbleTypeaheadOption<PropertyTag>) {
    formik.setSubmitting(true);

    let shouldContinue = await Alert.question(
      `Are you sure you want to remove the tag` +
        ` [${opt.item.name}] from all its associated contents?`
    );

    if (!shouldContinue) return;

    formik.setSubmitting(true);

    try {
      await updateContentTags(
        property._id,
        (property.contentTags || []).filter(t => t._id !== opt.item._id)
      );

      setFieldValue(
        'tags',
        values.tags.filter(t => t.item._id !== opt.item._id)
      );

      await refreshActiveProperty();
    } finally {
      formik.setSubmitting(false);
    }
  }

  function handleSegmentChange(segmentId: string) {
    let selectedSegment;

    if (segmentId !== 'unassigned') {
      selectedSegment = brand.contentSegments.find(s => s._id === segmentId);

      if (!selectedSegment) {
        // something went wrong: we cannot find the segment id in the brand's segments
        throw new Error(`Could not find segment ${segmentId}`);
      }
    }

    setFieldValue('segment', selectedSegment?._id);
  }

  const locked =
    ['create', 'edit'].includes(props.mode.type) &&
    !canModifyMultiPropertyItem(user, serverInfo, property._id, props.mode.edit);
  const readOnly = props.mode.type === 'view' || locked;
  const disabled = readOnly || locked || formik.isSubmitting || !formik.isValid || !dirty;

  const formActions = locked ? (
    <></>
  ) : (
    <div className="flex items-center">
      <div></div>
      <div className="ml-auto" />
      <BubbleButton disabled={disabled} variant="navy" type="reset" inverse icon="rotate-left">
        Reset
      </BubbleButton>
      <BubbleButton
        variant="navy"
        type="submit"
        disabled={disabled}
        icon={props.mode.type === 'edit' ? 'floppy-disk' : 'sparkles'}
      >
        {props.mode.type === 'edit' ? 'Save Changes' : 'Create Content'}
      </BubbleButton>
    </div>
  );

  const portalButtons = props.actionsElem
    ? createPortal(formActions, props.actionsElem)
    : formActions;

  useImperativeHandle(ref, () => ({
    reset: () => formik.resetForm(),
    submit: () => formik.submitForm(),
  }));

  useEffect(() => {
    if (props.onStateUpdate)
      props.onStateUpdate({
        values: formik.values,
        disabled,
      });
  }, [formik.values, disabled, props.mode]);

  const zonesDict = mapBy(property.zones, '_id');

  return (
    <FormikProvider value={formik}>
      <fieldset disabled={readOnly} className={subnavClasses.join(' ')}>
        {!props.skipBasic && (
          <BubbleButton
            block
            appearance="squared"
            variant={values.type === 'basic' ? 'navy' : 'smoke'}
            onClick={() => setFieldValue('type', 'basic')}
          >
            <FontAwesomeIcon
              icon={values.type === 'basic' ? 'circle-dot' : 'circle'}
              fixedWidth
              className="mr-1"
            />
            Basic
          </BubbleButton>
        )}
        <BubbleButton
          block
          appearance="squared"
          variant={values.type === 'offer' ? 'navy' : 'smoke'}
          onClick={() => setFieldValue('type', 'offer')}
        >
          <FontAwesomeIcon
            icon={values.type === 'offer' ? 'circle-dot' : 'circle'}
            fixedWidth
            className="mr-1"
          />
          Offer
        </BubbleButton>
        <BubbleButton
          block
          appearance="squared"
          variant={values.type === 'message' ? 'navy' : 'smoke'}
          onClick={() => setFieldValue('type', 'message')}
        >
          <FontAwesomeIcon
            icon={values.type === 'message' ? 'circle-dot' : 'circle'}
            fixedWidth
            className="mr-1"
          />
          Message
        </BubbleButton>
        <BubbleButton
          block
          appearance="squared"
          variant={values.type === 'info' ? 'navy' : 'smoke'}
          onClick={() => setFieldValue('type', 'info')}
        >
          <FontAwesomeIcon
            icon={values.type === 'info' ? 'circle-dot' : 'circle'}
            fixedWidth
            className="mr-1"
          />
          Information
        </BubbleButton>
      </fieldset>
      <Form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 gap-4">
          {locked && (
            <BubbleAlert className="mb-3" variant="ocean">
              <span className="font-semibold">This template cannot be modified.</span> It may be a
              brand level event or you lack library permissions.
            </BubbleAlert>
          )}
          {values.type === 'offer' && props.mode.type !== 'filter' && (
            <BubbleFormGroup label="Images">
              <BubbleImageSelector
                maxItems={3}
                readOnly={readOnly}
                value={values.images || []}
                onValue={images => setFieldValue('images', images)}
              />
            </BubbleFormGroup>
          )}
          <BubbleFormGroup
            label={`Name ${values.type === 'basic' ? '(Internal)' : '(Public)'}`}
            asterisk
            error={fme(errors, touched, 'name')}
          >
            <BubbleInput
              block
              type="text"
              name="name"
              readOnly={readOnly}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.name}
            />
          </BubbleFormGroup>
          {['offer', 'message', 'info'].includes(values.type || '') && (
            <>
              {props.mode.type === 'filter' ? (
                <BubbleFormGroup label="Description">
                  <BubbleTextArea
                    block
                    name="descriptionFilter"
                    readOnly={readOnly}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.descriptionFilter}
                  />
                </BubbleFormGroup>
              ) : (
                <>
                  <BubbleFormGroup
                    label="Description"
                    asterisk
                    error={fme(errors, touched, 'description')}
                  >
                    <Editor
                      textareaName="content"
                      value={values.description || ''}
                      onValue={val => {
                        handleChange({ target: { name: 'description', value: val } });
                      }}
                      onBlur={evt => {
                        evt.target.name = 'description';
                        handleBlur(evt);
                      }}
                      mode="text-link"
                      disabled={readOnly}
                    />
                  </BubbleFormGroup>
                  {values.type === 'info' && (
                    <BubbleFormGroup label="Appearance">
                      <BubbleSelect
                        block
                        allowUndefined={false}
                        labelExtractor={o => camelToSentenceCase(o)}
                        options={ALL_INFO_CONTENT_APPEARANCES}
                        name="appearance"
                        readOnly={readOnly}
                        onValue={val => setFieldValue('appearance', val)}
                        onBlur={handleBlur}
                        value={values.appearance}
                      />
                    </BubbleFormGroup>
                  )}
                </>
              )}
            </>
          )}
          {props.mode.type != 'filter' && (
            <BubbleFormGroup label="Print Messaging">
              {props.mode.type !== 'view' && (
                <div className="text-right -mt-6 mb-1 relative">
                  <BubbleInput
                    className="mr-1"
                    type="checkbox"
                    name="showHtml"
                    inputClassName="skip-errors stretched-link"
                    onChange={handleChange}
                    checked={values.showHtml}
                  />
                  <span>Show HTML</span>
                </div>
              )}
              {values.showHtml ? (
                <BubbleTextArea
                  block
                  name="content"
                  readOnly={readOnly}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.content}
                />
              ) : (
                <Editor
                  textareaName="content"
                  value={values.content}
                  onValue={val => {
                    handleChange({ target: { name: 'content', value: val } });
                  }}
                  onBlur={evt => {
                    evt.target.name = 'content';
                    handleBlur(evt);
                  }}
                  disabled={readOnly}
                />
              )}
            </BubbleFormGroup>
          )}
          {property.settings?.useLegacyDdpContentSrc && (
            <div className="flex items-center relative">
              <BubbleInput
                className="mr-1"
                type="checkbox"
                name="hideFromDigitalDailyProgram"
                inputClassName="skip-errors stretched-link"
                readOnly={readOnly}
                onChange={handleChange}
                onBlur={handleBlur}
                checked={!!values.hideFromDigitalDailyProgram}
              />
              <span>Hide from Digital Daily Program</span>
            </div>
          )}
          <BubbleFormGroup label="Tags">
            <BubbleTypeahead
              block
              placeholder="Search... (or create a new one)"
              menuClassName="max-h-80 overflow-y-auto"
              labelExtractor={opt => opt.name}
              value={values.tags}
              options={contentTagOpts}
              readOnly={readOnly}
              allowNew
              onAdd={t => setFieldValue('tags', [...values.tags, t])}
              onRemove={t =>
                setFieldValue(
                  'tags',
                  values.tags.filter(t2 => t.key !== t2.key)
                )
              }
              onClearAll={() => setFieldValue('tags', [])}
              onCreate={handleOnCreateTag}
              onTrash={handleOnRemoveTag}
            />
          </BubbleFormGroup>
          {brand.contentSegments.length > 0 && (
            <BubbleFormGroup label="Segment">
              <BubbleSelect
                block
                allowUndefined
                readOnly={readOnly}
                labelExtractor={segment => segment.name}
                value={formik.values.segment ? segmentsMap[formik.values.segment] : undefined}
                options={brand.contentSegments}
                onValue={val => handleSegmentChange(val?._id ?? 'unassigned')}
              />
            </BubbleFormGroup>
          )}
          {brand.contentLevels.length > 0 && (
            <BubbleFormGroup label="Levels">
              <BubbleTypeaheadSimple
                block
                readOnly={readOnly}
                labelExtractor={id => levelsMap[id].name}
                value={formik.values.levels}
                onValue={levels => setFieldValue('levels', levels)}
                menuClassName="max-h-80 overflow-y-auto"
                options={brand.contentLevels.map(l => l._id)}
                placeholder="Search..."
              />
            </BubbleFormGroup>
          )}
          {brand.contentAttributes.length > 0 && (
            <BubbleFormGroup label="Attributes">
              <BubbleTypeaheadSimple
                block
                readOnly={readOnly}
                labelExtractor={id => attributesMap[id].name}
                value={formik.values.attributes}
                onValue={attributes => setFieldValue('attributes', attributes)}
                menuClassName="max-h-80 overflow-y-auto"
                options={brand.contentAttributes.map(a => a._id)}
                placeholder="Search..."
              />
            </BubbleFormGroup>
          )}
          {props.mode.type !== 'filter' && (
            <BubbleFormGroup className="mt-5" label="Call to Actions">
              <CallToActions
                readOnly={readOnly}
                callToActions={values.callToActions}
                onUpdate={cta => setFieldValue('callToActions', cta)}
              />
            </BubbleFormGroup>
          )}
          {props.mode.type === 'filter' ? (
            <BubbleFormGroup label="Audience" className="flex-grow">
              <BubbleSelect
                block
                allowUndefined
                undefinedLabel="All"
                labelExtractor={id => zonesDict[id].name}
                value={values.audienceZoneFilter}
                options={property.zones.map(z => z._id)}
                onValue={val => setFieldValue('audienceZoneFilter', val)}
              />
            </BubbleFormGroup>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <BubbleFormGroup label="Endpoints">
                <McmVisibilityTable
                  isTemplate
                  sourceMcm={values.mcm}
                  parentMcm={undefined}
                  onUpdateMcm={m => setFieldValue('mcm', m)}
                  readOnly={readOnly}
                />
              </BubbleFormGroup>
              <BubbleFormGroup label="Audience">
                <AudienceVisibilityTable
                  isTemplate
                  sourceAud={values.audience}
                  parentAud={undefined}
                  onUpdateAudience={m => setFieldValue('audience', m)}
                  readOnly={readOnly}
                />
              </BubbleFormGroup>
            </div>
          )}
        </div>
        {props.mode.type !== 'view' && (
          <div className={actionClasses.join(' ')}>{portalButtons}</div>
        )}
      </Form>
    </FormikProvider>
  );
}

export default forwardRef<ContentTemplateFormikRef, ContentTemplateFormikProps>(
  ContentTemplateFormik
);
